'use client';

import { Membership } from '@shared/types';
import {
    PublicBlendedCourse,
    PublicClassCourse,
    PublicEducator,
    PublicOccurrence,
    PublicOnDemandCourse,
    PublicOnRequestCourse,
} from '@cademy-package/sdk-component-types';
import { FunctionComponent, useMemo } from 'react';
import styles from './styles.module.scss';
import MembershipRequiredWarning, {
    MembershipFreeDiscountedAccessMessage,
    useIsMembershipRequired,
} from './components/MembershipRequired';
import { EnrolledAccessButton } from './components/EnrolledAccessButton';
import { useIam } from '../../services/IAm/hooks';
import { SpreadTheCost } from './components/SpreadTheCost';
import { useEnrolments } from '../../pages_components/[educator_param]/[course_param]/useEnrolments';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useIsMarketplace } from '../../contexts/AppContext/useIsMarketplace';
import { PrimaryBookButton } from './components/PrimaryBookButton';
import { DisplayPrice } from './components/DisplayPrice';
import { SecondaryButtons } from './components/SecondaryButtons';
import { useIsEmbed } from '../../contexts/AppContext/useIsEmbed';
import Card from '../Card';
import GuaranteesCard from './components/GuaranteesCard';
import { MarketplaceOnly } from '../AppContext/MarketplaceOnly';
import { useSavedCoursesState } from '../../hooks/useSavedCoursesState';
import { ActionButtons } from './components/ActionButtons';
import { SubscriptionBenefitsBadges } from './components/SubscriptionBenefitsBadges';
import { isChildOccurrence } from '../../services/occurrences/isChildOccurrence';
import classNames from 'classnames';

export type BookAsideProps = {
    course: PublicClassCourse | PublicOnRequestCourse | PublicOnDemandCourse | PublicBlendedCourse;
    educator: PublicEducator;
    occurrences: Array<PublicOccurrence>;
    memberships: Array<Membership>;
    vouchersAppInstalled: boolean;
    className?: string;
};

export const BookAside: FunctionComponent<BookAsideProps> = ({
    course,
    educator,
    occurrences,
    memberships,
    vouchersAppInstalled,
    className,
}) => {
    const queryClient = useMemo(() => {
        return new QueryClient();
    }, []);
    const { iam } = useIam();
    const { requiredMemberships, membershipRequired } = useIsMembershipRequired(
        iam,
        course,
        memberships
    );
    const enrolments = useEnrolments(iam, course._id);
    const isMarketplace: boolean = useIsMarketplace();
    const isEmbed: boolean = useIsEmbed();
    const { data: savedCoursesState } = useSavedCoursesState({ courseIds: [course._id] });

    const bookableOccurrences: Array<PublicOccurrence> = useMemo(() => {
        return occurrences.filter((occurrence) => {
            if (!isChildOccurrence(occurrence)) {
                return true;
            }
            if (occurrence.individually_bookable) {
                return true;
            }
            return false;
        });
    }, [occurrences]);

    const isFreeCourse = useMemo(() => {
        if (!('offers' in course)) {
            return false;
        }

        const offers = course.offers;
        const anyOfferCostsMoney = offers.some((offer) => {
            return offer.price > 0;
        });

        return !anyOfferCostsMoney;
    }, [course]);

    return (
        <QueryClientProvider client={queryClient}>
            <div className={classNames(styles.bookAsideContainer, className)}>
                <div className={styles.bookAsideStickyContainer}>
                    <ActionButtons courseId={course._id} saved={savedCoursesState[course._id]} />
                    <Card>
                        <article tabIndex={0} aria-labelledby="bookaside-header">
                            <h2 id="bookaside-header" className="screen_reader_hidden">
                                Booking options
                            </h2>
                            <DisplayPrice
                                course={course}
                                educator={educator}
                                occurrences={occurrences}
                            />
                            <SubscriptionBenefitsBadges
                                educator={educator}
                                subscriptionBenefits={enrolments.subscriptionBenefits}
                            />
                            <EnrolledAccessButton
                                course={course}
                                enrolmentId={enrolments.enrolmentId}
                                educatorSlug={educator.slug}
                            />
                            <MembershipRequiredWarning
                                iam={iam}
                                course={course}
                                requiredMemberships={requiredMemberships}
                                membershipRequired={membershipRequired}
                                educator={educator}
                            />
                            <PrimaryBookButton
                                bookableOccurrences={bookableOccurrences}
                                course={course}
                                educator={educator}
                                membershipRequired={membershipRequired}
                            />
                            <SecondaryButtons
                                course={course}
                                educator={educator}
                                isEmbed={isEmbed}
                                isMarketplace={isMarketplace}
                                isVoucherAppInstalled={vouchersAppInstalled}
                                occurrences={bookableOccurrences}
                            />
                        </article>
                    </Card>
                    <SpreadTheCost
                        educatorSlug={educator.slug}
                        courseSlug={course.slug}
                        isFree={isFreeCourse}
                    />
                    <MarketplaceOnly>
                        <GuaranteesCard />
                    </MarketplaceOnly>
                    <MembershipFreeDiscountedAccessMessage
                        iam={iam}
                        course={course}
                        memberships={memberships}
                        membershipRequired={membershipRequired}
                        educator={educator}
                    />
                </div>
            </div>
        </QueryClientProvider>
    );
};
